import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Grid, Paper, Box, Container, Card, Button, Link, Typography, ListItemText, Divider, List, ListItem } from "@mui/material";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { updateQuantity, removeItem, calculateTotalAmount } from "store/cartSlice";
import CartItem from "components/CartItems/CartItem";
// Correctly import AddressPage (assuming it's the default export)
import AddressPage from "./AddressPage";
import { RxCrossCircled } from "react-icons/rx";

import MakePayment from "./Payment";
import NoItem from "components/Miscellaneous/NoItem";
import CouponSection from "components/Coupon/Coupon";
import { addOrder } from "app/services/order/orderAPI";
import { IOrder, IOrderProduct } from "app/order"; // Import IOrderProduct
import { findUserId } from "app/services/UtilServices";
import { SERVICE_CHARGE, FIXED_DELIVERY_FEE } from "common/constant";
import OrderConfirm from "components/OrderConfirmation/OrderConfirm";
import { findUserDetails, storeFCM } from "app/services/customer/customerAPI";
import Spinner from "components/Miscellaneous/Spinner";
import { requestForToken } from "common/firebase";
import { IUserAddress } from "app/address";

// Define the structure of items coming from Redux store based on CartItem props
interface IReduxCartItemProduct {
    id: string;
    title: string;
    unitLabel: string;
    sellingPrice: number;
    imageUrl: string;
    // Add other potential product fields if needed based on createOrderPayload usage
    categoryIds?: string[];
    shopId?: string; // Assuming shopId might be here
}

interface IReduxCartItem {
    product: IReduxCartItemProduct;
    quantity: number;
}


const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
}));

// Define RootState placeholder (replace with actual RootState type if available)
type RootState = any;

const CartPage: React.FC = () => {
    const [deliverTips, setDeliveryTips] = React.useState<number>(0);
    const [activeStep, setActiveStep] = React.useState<number>(0);
    const [discount, setDiscount] = React.useState<number>(0);
    const [order, setOrder] = React.useState<IOrder | null>(null); // Type as IOrder | null
    const [paymentObj, setPaymentObj] = React.useState<any>(null); // Type as any or specific type if known
    const [loading, setLoading] = React.useState<boolean>(false);
    const [addressData, setAddressData] = React.useState<IUserAddress | null>(null); // Type as IUserAddress | null
    const [useAddress, setUseAddress] = React.useState<boolean>(false);

    const [isAdressUpdated, setIsAdressUpdated] = React.useState(false);
    const [selectedAddress, setSelectedAddress] = React.useState<IUserAddress | null>(null);
    // Add this handler function
    const handleAddressSelection = (address: IUserAddress) => {
        setSelectedAddress(address);
        setIsAdressUpdated(true); // This will enable the button
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const dispatch = useDispatch();
    // TODO: Replace 'any' with RootState type from Redux store setup
    const cartItems = useSelector((state: RootState) => state.cart.items as IReduxCartItem[]); // Use IReduxCartItem

    useEffect(() => {
        document.title = "HatBazaar | Checkout ";
        // Calculation seems off, should use item.product.sellingPrice
        cartItems.reduce(
            (total: number, item: IReduxCartItem) => total + item.product.sellingPrice * item.quantity, // Use correct structure
            0
        );
        dispatch(calculateTotalAmount());
        dispatch(calculateTotalAmount());
        setActiveStep(0);
    }, [cartItems, dispatch]);

    const handleQuantityChange = (id: string, quantity: number) => {
        dispatch(updateQuantity({ id: id, quantity }));
        dispatch(calculateTotalAmount());
        if (quantity !== 0 && quantity < 7) {
            toast.success(`Item quantity is changed to ${quantity}`);
        }
        if (quantity < 1) {
            toast.warn(`Item removed from the cart`);
        }
    };

    const handleRemoveFromCart = (id: string) => {
        dispatch(removeItem({ productId: id }));
        dispatch(calculateTotalAmount());
        toast.warn(`Item removed from the cart`);
    };
    // TODO: Replace 'any' with RootState type from Redux store setup
    const totalAmount = useSelector((state: RootState) => state.cart.totalAmount as number);
    const totalItems = cartItems.reduce(
        (total: number, item: IReduxCartItem) => total + item.quantity, // Use IReduxCartItem
        0
    );

    const handleTip = (amount: number) => {
        setDeliveryTips(deliverTips + amount);
        toast.success(`Your tip ${amount} rupess for delivery partner is added`);
    };

    const RemoveTip = () => {
        setDeliveryTips(0);
        toast.warn(`Your have removed partner delivery Tip`);
    };

    const RemoveCoupon = () => {
        setDiscount(0);
        toast.warn(`Your have removed offer coupon!`);
    };

    const createOrder = async () => {
        try {
            setLoading(true);
            const orderPayload: IOrder = createOrderPayload(cartItems);
            const result = await addOrder(orderPayload);
            setLoading(false);
            if (result.data) {
                setOrder(result.data);
                handleNext();
            }
        } catch (error) {
            console.log(error);
        }
    };

    function createOrderPayload(cartData: IReduxCartItem[]): IOrder { // Use IReduxCartItem[]
        const userId = findUserId();
        if (!userId && cartData.length > 0) {
            throw new Error("Could not verify your details");
        }
        // Ensure all required fields for IOrder are provided
        const orderPayload: IOrder = {
            userId: userId || '',
            shopId: cartData[0]?.product?.shopId || '', // Get shopId from the first product if available
            products: [],
            paymentMethod: "upi",
            paymentStatus: "pending",
            serviceCharge: SERVICE_CHARGE,
            effectiveDeliveryCharge: FIXED_DELIVERY_FEE,
            deliveryCharge: FIXED_DELIVERY_FEE,
            status: "1",
            orderTime: new Date(),
            totalPrice: totalAmount,

        };

        let calculatedTotalPrice = 0;

        cartData.forEach((cartItem: IReduxCartItem) => { // Use IReduxCartItem
            console.log(cartItem);
            const product = cartItem.product; // Access the nested product object
            const itemTotalPrice = product.sellingPrice * cartItem.quantity;
            calculatedTotalPrice += itemTotalPrice;

            const item: IOrderProduct = {
                productId: product.id,
                totalPrice: itemTotalPrice, // Use calculated item total price
                quantity: cartItem.quantity,
                priceUnit: product.unitLabel, // Use unitLabel from product
                unitLabel: product.unitLabel, // Use unitLabel from product
                buyingPriceRate: product.sellingPrice, // Use sellingPrice
                title: product.title, // Use title from product
                optionId: product.categoryIds?.[0] || '', // Use categoryIds from product
                // Set defaults or calculate other fields
                effectivePriceRate: product.sellingPrice, // Default to sellingPrice
                discount: 0, // Default
                totalEffectivePrice: itemTotalPrice, // Default to itemTotalPrice
                // sellingPrice: product.sellingPrice, // Not part of IOrderProduct
                // id: product.id, // Not part of IOrderProduct
                // imageUrl: product.imageUrl, // Not part of IOrderProduct
                // categoryIds: product.categoryIds || [], // Not part of IOrderProduct
            };
        });

        // Optionally update orderPayload.totalPrice if it should be the calculated sum
        orderPayload.totalPrice = calculatedTotalPrice;
        // Optionally calculate and set orderPayload.totalEffectivePrice
        orderPayload.totalEffectivePrice = calculatedTotalPrice + (orderPayload.serviceCharge || 0) + orderPayload.effectiveDeliveryCharge - (discount || 0); // Example calculation

        return orderPayload;
    }

    React.useEffect(() => {
        fetchUser();
    }, []);

    const fetchUser = async () => {
        try {
            if (!findUserId()) {
                return toast.warn("Login to continue");
            }
            const userId = findUserId();
            if (!userId) return; // Guard clause if userId is null/undefined
            const result = await findUserDetails(userId);
            // Assuming result.data is an array of users and each user has an address array
            if (result?.success && result.data && result.data.length > 0 && result.data[0].address && result.data[0].address.length > 0) {
                const user = result.data[0];
                setAddressData(user.address[0]); // Set IUserAddress | null
                setUseAddress(false);
                if (user && !user.fcmDeviceToken) {
                    const fcmToken = await requestForToken();
                    if (fcmToken && user.id) { // Ensure user.id exists
                        await storeFCM(user.id, fcmToken);
                    }
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    const ValidTotal = () => {
        let totalAmt =
            totalAmount +
            SERVICE_CHARGE +
            FIXED_DELIVERY_FEE +
            deliverTips -
            discount;
        if (totalAmt <= 0) {
            totalAmt = 1;
        }
        return totalAmt;
    };

    return (
        <>
            <Container style={{ marginTop: "220px", marginBottom: "300px" }}>
                {activeStep === 5 ? (
                    <OrderConfirm paymentObj={paymentObj} />
                ) : (
                    <>
                        {cartItems.length === 0 ? (
                            <NoItem />
                        ) : (
                            <Box>
                                <Grid
                                    container
                                    spacing={2}
                                    style={{ justifyContent: "center" }}
                                >
                                    {activeStep <= 2 && (
                                        <Grid item xs={12} md={8}>
                                            <div>
                                                <h3>
                                                    Cart ({totalItems} Items){" "}
                                                    <span style={{ color: "green" }}>
                                                        ₹{totalItems * 6} saved on this order{" "}
                                                    </span>{" "}
                                                </h3>
                                            </div>
                                            {activeStep <= 2 && (
                                                <>
                                                    <div style={{ overflow: "auto" }}>
                                                        <TableContainer component={Paper}>
                                                            <Table
                                                                stickyHeader
                                                                sx={{ minWidth: 650 }}
                                                                aria-label="Cart Items table"
                                                            >
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>Item photo</TableCell>
                                                                        <TableCell>Item</TableCell>
                                                                        <TableCell>Unit</TableCell>
                                                                        <TableCell>Amount</TableCell>
                                                                        <TableCell>   Action</TableCell>
                                                                    </TableRow>
                                                                </TableHead>

                                                                <TableBody>
                                                                    {cartItems.map((cartItem: IReduxCartItem) => ( // Use IReduxCartItem
                                                                        <CartItem
                                                                            key={cartItem.product.id} // Use product id as key
                                                                            item={cartItem.product} // Pass the nested product object
                                                                            quantity={cartItem.quantity} // Pass quantity
                                                                            onQuantityChange={handleQuantityChange}
                                                                            onRemoveFromCart={handleRemoveFromCart}
                                                                        />
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    </div>
                                                    <Item style={{ marginTop: "1rem" }}>
                                                        <Typography variant="h6" gutterBottom>
                                                            Delivery Partner Tip
                                                        </Typography>
                                                        <p>
                                                            {" "}
                                                            The entire amount will be sent to your delivery
                                                            partner
                                                        </p>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                textAlign: "center",
                                                                padding: "5px",
                                                                margin: "5px",
                                                                width: "100%",
                                                            }}
                                                        >
                                                            <Button
                                                                color="secondary"
                                                                onClick={() => handleTip(5)}
                                                                style={{ borderRadius: "10px", color: "white" }}
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<VolunteerActivismIcon />}
                                                            >
                                                                ₹5
                                                            </Button>

                                                            <Button
                                                                color="secondary"

                                                                onClick={() => handleTip(10)}
                                                                style={{ borderRadius: "10px", color: "white" }}
                                                                size="medium"
                                                                variant="contained"
                                                                startIcon={<VolunteerActivismIcon />}
                                                            >
                                                                ₹10
                                                            </Button>

                                                            <Button
                                                                color="secondary"

                                                                onClick={() => handleTip(20)}
                                                                style={{ borderRadius: "10px", color: "white" }}
                                                                size="medium"
                                                                variant="contained"
                                                                startIcon={<VolunteerActivismIcon />}
                                                            >
                                                                ₹20
                                                            </Button>
                                                        </div>
                                                    </Item>
                                                </>
                                            )}
                                        </Grid>
                                    )}
                                    <Grid item xs={12} md={4} style={{ textAlign: "left" }}>
                                        <Item
                                            style={{
                                                justifyContent: "space-between",
                                                display: "flex",
                                            }}
                                        >
                                            <div style={{ display: "flex" }}>
                                                <CardGiftcardOutlinedIcon
                                                    fontSize="large"
                                                    color="secondary"
                                                />
                                                <Typography variant="h6" gutterBottom>
                                                    Avail Offers / Coupons
                                                </Typography>
                                            </div>

                                            <div>
                                                <CouponSection
                                                    setDiscount={setDiscount}
                                                    discount={discount}
                                                />
                                            </div>
                                        </Item>

                                        <Item>
                                            <List sx={{ width: "100%" }}>
                                                <ListItem>
                                                    <ListItemText primary="Item Amount" />
                                                    <ListItemText style={{ textAlign: "end" }}>
                                                        <b>₹{totalAmount}</b>
                                                    </ListItemText>
                                                </ListItem>
                                                <Divider />

                                                <ListItem>
                                                    <ListItemText
                                                        primary="Handling Fee"
                                                        secondary="Packing & Small cart fee"
                                                    />
                                                    <ListItemText style={{ textAlign: "end" }}>
                                                        <p>
                                                            <s>₹15</s>
                                                            <span style={{ color: "346ca7b" }}>
                                                                ₹{SERVICE_CHARGE}
                                                            </span>
                                                        </p>
                                                    </ListItemText>
                                                </ListItem>
                                                <Divider />
                                                <ListItem>
                                                    <ListItemText primary="Delivery Fee" secondary="" />
                                                    <ListItemText style={{ textAlign: "end" }}>
                                                        <p>
                                                            <s>₹20</s>
                                                            <span style={{ color: "346ca7b" }}>
                                                                ₹{FIXED_DELIVERY_FEE}
                                                            </span>
                                                        </p>
                                                    </ListItemText>
                                                </ListItem>
                                                <Divider />
                                                {deliverTips > 0 && (
                                                    <>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary="Delivery Partner Tip"
                                                                secondary="Thank you for your love!"
                                                            />
                                                            <ListItemText style={{ textAlign: "end" }}>
                                                                <RxCrossCircled
                                                                    color="red"
                                                                    onClick={RemoveTip}
                                                                />
                                                            </ListItemText>
                                                            <ListItemText style={{ textAlign: "end" }}>
                                                                <p>₹{deliverTips}</p>
                                                            </ListItemText>
                                                        </ListItem>
                                                        <Divider />
                                                    </>
                                                )}
                                                {discount > 0 && (
                                                    <>
                                                        <ListItem>
                                                            <ListItemText
                                                                primary="Coupon discount"
                                                                secondary="Total discount"
                                                            />
                                                            <ListItemText style={{ textAlign: "end" }}>
                                                                <RxCrossCircled
                                                                    color="red"
                                                                    onClick={RemoveCoupon}
                                                                />
                                                            </ListItemText>
                                                            <ListItemText style={{ textAlign: "end" }}>
                                                                <p style={{ color: "346ca7b" }}>
                                                                    ₹{discount}
                                                                </p>
                                                            </ListItemText>
                                                        </ListItem>
                                                        <Divider />
                                                    </>
                                                )}

                                                <ListItem>
                                                    <ListItemText primary="To Pay" />
                                                    <ListItemText style={{ textAlign: "end" }}>
                                                        <h3>₹ {ValidTotal()}</h3>
                                                    </ListItemText>
                                                </ListItem>
                                                {activeStep === 0 ? (
                                                    <Button
                                                        variant="contained"
                                                        style={{
                                                            color: "white",
                                                            textAlign: "center",
                                                            borderRadius: "10px",
                                                        }}
                                                        size="large"
                                                        onClick={() => handleNext()}
                                                    >
                                                        Procced to buy
                                                    </Button>
                                                ) : null}
                                            </List>
                                        </Item>
                                        {activeStep === 1 ||
                                            activeStep === 2 ||
                                            activeStep === 3 ? (
                                            <div
                                                style={{
                                                    textAlign: "center",
                                                    justifyContent: "center",
                                                    margin: "auto",
                                                }}
                                            >
                                                {activeStep === 1 && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <AddressPage
                                                            setIsAdressUpdated={setIsAdressUpdated}
                                                        // Pass other required props if AddressPage expects them
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            fullWidth
                                                            style={{
                                                                color: "white",
                                                                textAlign: "center",
                                                                borderRadius: "10px",
                                                                marginTop: "16px"
                                                            }}
                                                            size="large"
                                                            onClick={() => {
                                                                handleNext();
                                                                toast.success("Delivery address confirmed");
                                                            }}
                                                            disabled={isAdressUpdated} // Now ties directly to address selection
                                                        >
                                                            Deliver to this address
                                                        </Button>
                                                    </Box>
                                                )}


                                                {activeStep === 2 ? (
                                                    <>
                                                        {loading ? (
                                                            <Spinner />
                                                        ) : (
                                                            <Button
                                                                variant="contained"
                                                                style={{
                                                                    color: "white",
                                                                    textAlign: "center",
                                                                    borderRadius: "10px",
                                                                }}
                                                                size="large"
                                                                onClick={() => createOrder()}
                                                            >
                                                                Continue to payment
                                                            </Button>
                                                        )}
                                                    </>
                                                ) : null}

                                                {activeStep === 3 ? (
                                                    <>
                                                        {/* Pass only the required fields for MakePayment's order prop */}
                                                        {order && ( // Render only if order is not null
                                                            <MakePayment
                                                                // Ensure the passed object matches the expected structure in MakePayment
                                                                order={{
                                                                    rzpPayId: order.rzpPayId || '',
                                                                    id: order.id || order._id || '', // Use _id as fallback if id is missing
                                                                    rzpOrderId: order.rzpOrderId || '',
                                                                    orderNo: order.orderNo || '',
                                                                    orderTime: order.orderTime,
                                                                }}
                                                                paymentObj={paymentObj}
                                                                setPaymentObj={setPaymentObj}
                                                                setActiveStep={setActiveStep}
                                                            />
                                                        )}
                                                        <Card>
                                                            <div style={{ margin: "5px", padding: "10px" }}>
                                                                <p>
                                                                    <span style={{ color: "red" }}>Note:</span> No
                                                                    refund for cancellation made after order
                                                                    accepted by restaurant/shop. Avoid
                                                                    cancellation as it leads to food wastage.
                                                                </p>
                                                                <Link target="_blank" href="/terms-condition">
                                                                    Read cancellation policy
                                                                </Link>
                                                            </div>
                                                        </Card>
                                                    </>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </>
                )}
            </Container>
        </>
    );
};

export default CartPage;
